import { Card } from "@/components/ui/card";
import LanguageSwitch from "@/components/LanguageSwitch";
import { aiCallContent } from "@/data/aiCallContent";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { getPageLocale, getPageMarket } from "@/utils/pageLocale";
import SEO from "@/components/SEO";
import { Clock3 } from "lucide-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/utils/posthog";
import { getPromptLibraryContext } from "@/utils/promptLibraryConversion";
import { getJourneyHomeRevision } from "@/utils/organizationJourney";
import GrowthIntake from "@/components/GrowthIntake";
import { getGrowthJourneyContext } from "@/utils/growthJourney";

const GOOGLE_CALENDAR_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ1H47JNUu_WZJGKFxAknoYHGaZuC6FIQRYJIdaqd34KmkRpNos4N7SABAQ5QX9DGN50GmMABHto?gv=true";

const AICall = () => {
  const { pathname, search: routeSearch } = useLocation();
  const search = useHydratedSearch();
  const locale = getPageLocale(pathname);
  const market = getPageMarket(locale, search);
  const copy = aiCallContent[locale];
  const calendarUrl = locale === "pt-BR" ? `${GOOGLE_CALENDAR_URL}&hl=pt_BR` : GOOGLE_CALENDAR_URL;
  const searchParams = new URLSearchParams(search);
  const source = searchParams.get("source") || "direct";
  const sourceLocation = searchParams.get("location") || "unknown";
  const audience = "organization";
  const homeRevision = getJourneyHomeRevision(search);
  const libraryContext = getPromptLibraryContext(search);
  const selectedWorkflow = libraryContext?.workflow_title || searchParams.get("workflow");
  const utmSource = searchParams.get("utm_source") || source;
  const utmMedium = searchParams.get("utm_medium") || "website";
  const utmCampaign =
    searchParams.get("utm_campaign") ||
    "organization-ai-adoption";
  const utmContent = searchParams.get("utm_content") || sourceLocation;
  const utmTerm = searchParams.get("utm_term") || undefined;
  const isPartnerPricing = source === "openai-partner";
  const callCopy = isPartnerPricing
    ? copy.partner
    : copy.organization;

  useEffect(() => {
    if (search !== routeSearch) return;
    trackEvent("AI Strategy Call Page Viewed", {
      ...getGrowthJourneyContext(search),
      language: locale,
      market,
      source,
      source_location: sourceLocation,
      audience,
      home_revision: homeRevision,
      workflow: selectedWorkflow,
      ...getPromptLibraryContext(search),
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
    });
  }, [
    search,
    routeSearch,
    locale,
    market,
    selectedWorkflow,
    source,
    sourceLocation,
    audience,
    homeRevision,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
  ]);

  const handleCalendarLoad = () => {
    trackEvent("Google Calendar Embed Loaded", {
      ...getGrowthJourneyContext(search),
      language: locale,
      market,
      source,
      source_location: sourceLocation,
      audience,
      home_revision: homeRevision,
      workflow: selectedWorkflow,
      ...libraryContext,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
    });
  };

  return (
    <div className="min-h-screen bg-[#07111f] text-white">
      <SEO
        title={callCopy.seoTitle}
        description={callCopy.seoDescription}
        keywords={copy.keywords}
        languageAlternates={[
          { language: "en", path: "/ai-call" },
          { language: "pt-BR", path: "/pt-br/ai-call" },
          { language: "x-default", path: "/ai-call" },
        ]}
      />

      <header className="border-b border-white/10 bg-[#07111f]">
        <div className="mx-auto flex min-h-16 max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2 sm:px-6 lg:px-8">
          <a href="/" aria-label="Tech Leaders home">
            <img
              src="/orange-logo.png"
              alt="Tech Leaders"
              className="h-7 w-auto sm:h-10"
            />
          </a>
          <LanguageSwitch path="/ai-call" />
        </div>
      </header>

      <main className="relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "linear-gradient(to bottom, black 0%, rgba(0,0,0,.7) 60%, transparent 100%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-4xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-4 py-2 text-sm font-semibold text-orange-300">
              <Clock3 className="h-4 w-4" aria-hidden="true" />
              {callCopy.badge}
            </div>
            <h1 className="font-serif text-5xl font-semibold leading-[0.98] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
              {callCopy.headline}
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              {callCopy.intro}
            </p>
          </div>

          <div className="grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
            <div className="space-y-6">
              {selectedWorkflow && (
                <Card className="border-orange-400/30 bg-orange-400/10 p-5 text-white">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-orange-300">
                    {copy.workflowLabel}
                  </p>
                  <p className="mt-2 text-xl font-semibold">
                    {selectedWorkflow}
                  </p>
                </Card>
              )}

              <div className="space-y-4">
                {callCopy.outcomes.map((outcome) => {
                  const Icon = outcome.icon;
                  return (
                    <Card
                      key={outcome.title}
                      className="border-white/10 bg-white/[0.06] p-5 text-white shadow-none"
                    >
                      <div className="flex items-start gap-4">
                        <div className="rounded-lg bg-orange-400/10 p-2.5">
                          <Icon
                            className="h-5 w-5 text-orange-300"
                            aria-hidden="true"
                          />
                        </div>
                        <div>
                          <h2 className="font-semibold">{outcome.title}</h2>
                          <p className="mt-1 text-sm leading-relaxed text-slate-300">
                            {outcome.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <Card className="border-white/10 bg-white p-5 text-[#07111f]">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 overflow-hidden rounded-full">
                    <img
                      src="/todd-media-kit.png"
                      alt="Todd Larsen"
                      className="h-full w-full origin-[50%_25%] scale-[2.2] object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold">Todd Larsen</p>
                    <p className="text-sm text-slate-600">
                      {copy.founder}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {callCopy.hostNote}
                </p>
              </Card>

              <div className="overflow-hidden rounded-xl border border-white/10 shadow-2xl">
                {copy.videoLabel && <p className="px-3 py-2 text-sm text-slate-300">{copy.videoLabel}</p>}
                <iframe
                  className="aspect-video w-full"
                  src="https://www.youtube.com/embed/LyY-glR6P_8"
                  title={copy.videoTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>

            <Card className="order-first overflow-hidden border-white/10 bg-white text-[#07111f] shadow-2xl lg:order-none lg:sticky lg:top-6">
              <div className="border-b border-slate-200 px-5 py-5 sm:px-7">
                <h2 className="text-2xl font-bold">
                  {copy.chooseTime}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {callCopy.bookingDescription}
                </p>
                {copy.languageNote && <p className="mt-2 text-sm text-slate-600">{copy.languageNote}</p>}
              </div>

              <GrowthIntake search={search} locale={locale} />

              <iframe
                src={calendarUrl}
                title={callCopy.calendarTitle}
                className="h-[720px] w-full border-0 sm:h-[760px]"
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={handleCalendarLoad}
              />

              <div className="border-t border-slate-200 px-5 py-5 text-center sm:px-7">
                <p className="text-sm text-slate-600">{copy.calendarNotLoading}{" "}
                  <a
                    href={calendarUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-orange-600 hover:underline"
                  >
                    {copy.openCalendar}
                    </a>
                  .
                </p>
                <p className="mt-2 text-sm text-slate-600">{copy.noTimes}{" "}
                  <a
                    href="mailto:todd@technical-leaders.com"
                    className="font-semibold text-orange-600 hover:underline"
                  >
                    {copy.emailTodd}
                    </a>
                  .
                </p>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AICall;
